import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PublicNav } from '../components/PublicNav';
import { MessageGregBubble } from '../components/MessageGregBubble';
import { DemoLeadSection } from '../components/DemoLeadSection';
import { CompareCrossLink } from '../components/CompareCrossLink';
import { ALL_STATES, TOP_STATES } from '../data/states';
import { MapPin, ChevronRight, ArrowRight } from 'lucide-react';

const CANONICAL = 'https://k9os.app/dog-training-software';
const TITLE = 'Dog Training Software by Region — K9os App (USA, Canada &amp; Worldwide)';
const DESC = 'K9os App is the all-in-one dog training software available to dog trainers, board-and-train facilities, kennels, and dog training companies in every U.S. state, across Canada, and worldwide.';

const setMeta = (name, content, attr = 'name') => {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};
const setCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement('link'); el.setAttribute('rel', 'canonical'); document.head.appendChild(el); }
  el.setAttribute('href', href);
};
const upsertJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement('script'); el.type = 'application/ld+json'; el.id = id; document.head.appendChild(el); }
  el.textContent = JSON.stringify(data);
};

export default function StatesIndex() {
  useEffect(() => {
    const prev = document.title;
    document.title = TITLE;
    setMeta('description', DESC);
    setMeta('og:title', TITLE, 'property');
    setMeta('og:description', DESC, 'property');
    setMeta('og:url', CANONICAL, 'property');
    setMeta('twitter:title', TITLE);
    setMeta('twitter:description', DESC);
    setCanonical(CANONICAL);
    upsertJsonLd('k9os-states-index-jsonld', {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': `${CANONICAL}#page`,
      url: CANONICAL,
      name: TITLE,
      description: DESC,
      isPartOf: { '@id': 'https://k9os.app/#website' },
      hasPart: [
        { '@type': 'WebPage', name: 'Dog Training Software in Canada', url: `${CANONICAL}/canada` },
        { '@type': 'WebPage', name: 'Dog Training Software Worldwide', url: `${CANONICAL}/worldwide` },
        ...ALL_STATES.map((s) => ({
          '@type': 'WebPage',
          name: `Dog Training Software in ${s.name}`,
          url: `${CANONICAL}/${s.slug}`,
        })),
      ],
    });
    return () => {
      document.title = prev;
      const el = document.getElementById('k9os-states-index-jsonld');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className='min-h-screen bg-white dark:bg-slate-950' data-testid='states-index'>
      <PublicNav />

      {/* Breadcrumb */}
      <div className='border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900'>
        <nav aria-label='Breadcrumb' className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 text-xs text-slate-600 dark:text-slate-400 flex items-center gap-1'>
          <Link to='/' className='hover:text-[#1B3A87] dark:hover:text-[#93C5FD]'>Home</Link>
          <ChevronRight className='w-3 h-3 opacity-60' />
          <span className='text-slate-900 dark:text-slate-100 font-medium'>Dog Training Software by State</span>
        </nav>
      </div>

      {/* Hero */}
      <section className='bg-gradient-to-b from-white to-slate-50 dark:from-slate-950 dark:to-slate-900'>
        <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20'>
          <div className='inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1B3A87]/10 text-[#1B3A87] dark:bg-[#93C5FD]/10 dark:text-[#93C5FD] text-xs font-semibold uppercase tracking-[0.18em]'>
            <MapPin className='w-3.5 h-3.5' /> USA · Canada · Worldwide
          </div>
          <h1 className='mt-4 font-display text-3xl sm:text-4xl md:text-5xl font-bold text-[#0B1220] dark:text-slate-50 leading-tight max-w-4xl' data-testid='states-index-h1'>
            Dog Training Software by Region &mdash; K9os App for Every Dog Trainer
          </h1>
          <p className='mt-5 text-slate-600 dark:text-slate-300 text-base md:text-lg leading-relaxed max-w-3xl'>
            Pick your region to see how K9os App runs dog trainers, board-and-train facilities, kennels, and full dog training
            companies in your area &mdash; every U.S. state, all of Canada, plus international support for the UK, Australia,
            Europe, and beyond.
          </p>
        </div>
      </section>

      {/* Top 30 grid — surfaced first per user request so state seekers
          hit the state list immediately after the hero. */}
      <section className='py-12 md:py-16 border-t border-slate-200 dark:border-slate-800'>
        <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8'>
          <h2 className='font-display text-2xl md:text-3xl font-bold text-[#0B1220] dark:text-slate-50'>Top dog-training states</h2>
          <p className='mt-2 text-slate-600 dark:text-slate-300 text-sm md:text-base'>Highest concentration of dog trainers and dog training companies in the USA.</p>
          <div className='mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3' data-testid='states-top-grid'>
            {TOP_STATES.map((s) => (
              <Link
                key={s.slug}
                to={`/dog-training-software/${s.slug}`}
                className='group px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-[#C8102E] hover:shadow-md transition-all flex items-center justify-between gap-2'
                data-testid={`state-link-${s.slug}`}
              >
                <span className='text-sm font-semibold text-[#1B3A87] dark:text-[#93C5FD]'>{s.name}</span>
                <ArrowRight className='w-3.5 h-3.5 text-slate-400 group-hover:text-[#C8102E] transition-colors' />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* All 50 alphabetical — sits directly under the Top-tier grid. */}
      <section className='py-12 md:py-16 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800'>
        <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8'>
          <h2 className='font-display text-2xl md:text-3xl font-bold text-[#0B1220] dark:text-slate-50'>All 50 states (A &ndash; Z)</h2>
          <div className='mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3' data-testid='states-all-grid'>
            {ALL_STATES.map((s) => (
              <Link
                key={s.slug}
                to={`/dog-training-software/${s.slug}`}
                className='px-4 py-2 rounded-lg text-sm font-medium text-slate-800 dark:text-slate-100 hover:text-[#C8102E] dark:hover:text-[#F87171] hover:bg-white dark:hover:bg-slate-950 transition-colors flex items-center justify-between'
              >
                <span>{s.name}</span>
                <span className='text-[10px] tracking-wider text-slate-400'>{s.abbr}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* International — Canada + UK + Worldwide (now below the states) */}
      <section className='py-12 md:py-16 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950'>
        <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8'>
          <h2 className='font-display text-2xl md:text-3xl font-bold text-[#0B1220] dark:text-slate-50'>International coverage</h2>
          <p className='mt-2 text-slate-600 dark:text-slate-300 text-sm md:text-base'>
            K9os isn&rsquo;t US-only. K9os is ready to launch for dog trainers across Canada, the United Kingdom, and every other country worldwide &mdash; be the FIRST in your region.
          </p>
          <div className='mt-6 grid grid-cols-1 md:grid-cols-3 gap-5' data-testid='regions-grid'>
            <Link
              to='/dog-training-software/canada'
              className='group rounded-2xl border border-slate-200 dark:border-slate-800 bg-gradient-to-br from-[#1B3A87]/5 via-white to-[#1B3A87]/10 dark:from-[#1B3A87]/20 dark:via-slate-900 dark:to-[#1B3A87]/10 p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all'
              data-testid='region-link-canada'
            >
              <div className='inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white dark:bg-slate-950 border border-[#1B3A87]/25 text-[#1B3A87] dark:text-[#93C5FD] text-[11px] font-bold uppercase tracking-[0.18em]'>
                Canada &middot; CA
              </div>
              <h3 className='mt-3 font-display text-xl md:text-2xl font-bold text-[#0B1220] dark:text-slate-50 leading-snug'>Dog Training Software in Canada</h3>
              <p className='mt-2 text-sm text-slate-600 dark:text-slate-300'>Toronto, Montreal, Vancouver, Calgary, Ottawa &amp; every province &mdash; CAD payments via Stripe.</p>
              <div className='mt-4 inline-flex items-center gap-1 text-xs font-semibold text-[#C8102E]'>Open Canada page <ArrowRight className='w-3.5 h-3.5' /></div>
            </Link>
            <Link
              to='/dog-training-software/uk'
              className='group rounded-2xl border border-slate-200 dark:border-slate-800 bg-gradient-to-br from-[#1B3A87]/5 via-white to-[#C8102E]/10 dark:from-[#1B3A87]/20 dark:via-slate-900 dark:to-[#C8102E]/10 p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all'
              data-testid='region-link-uk'
            >
              <div className='inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white dark:bg-slate-950 border border-[#1B3A87]/25 text-[#1B3A87] dark:text-[#93C5FD] text-[11px] font-bold uppercase tracking-[0.18em]'>
                United Kingdom &middot; GB
              </div>
              <h3 className='mt-3 font-display text-xl md:text-2xl font-bold text-[#0B1220] dark:text-slate-50 leading-snug'>Dog Training Software in the UK</h3>
              <p className='mt-2 text-sm text-slate-600 dark:text-slate-300'>London, Manchester, Birmingham, Leeds, Glasgow, Edinburgh &mdash; GBP payments via Stripe.</p>
              <div className='mt-4 inline-flex items-center gap-1 text-xs font-semibold text-[#C8102E]'>Open UK page <ArrowRight className='w-3.5 h-3.5' /></div>
            </Link>
            <Link
              to='/dog-training-software/worldwide'
              className='group rounded-2xl border border-slate-200 dark:border-slate-800 bg-gradient-to-br from-[#C8102E]/5 via-white to-[#C8102E]/10 dark:from-[#C8102E]/20 dark:via-slate-900 dark:to-[#C8102E]/10 p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all'
              data-testid='region-link-worldwide'
            >
              <div className='inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white dark:bg-slate-950 border border-[#C8102E]/25 text-[#C8102E] dark:text-[#F87171] text-[11px] font-bold uppercase tracking-[0.18em]'>
                Worldwide &middot; International
              </div>
              <h3 className='mt-3 font-display text-xl md:text-2xl font-bold text-[#0B1220] dark:text-slate-50 leading-snug'>Dog Training Software Worldwide</h3>
              <p className='mt-2 text-sm text-slate-600 dark:text-slate-300'>Australia, New Zealand, Europe &amp; beyond &mdash; multi-currency support across 135+ regions.</p>
              <div className='mt-4 inline-flex items-center gap-1 text-xs font-semibold text-[#C8102E]'>Open Worldwide page <ArrowRight className='w-3.5 h-3.5' /></div>
            </Link>
          </div>
        </div>
      </section>

      {/* SEO cross-link to /compare — matches the pattern on every state
          / regional page. Sits above the DemoLeadSection. */}
      <CompareCrossLink regionName='the USA' />

      {/* Full lead-capture form — nationwide catch-all so trainers browsing
          the states directory can submit a demo request without navigating
          away to /contact. */}
      <DemoLeadSection
        regionName='the USA'
        regionSlug='states-index'
        eyebrow='Nationwide · schedule a demo'
        headingOverride='Book a free K9os demo — tailored to your dog-training business.'
      />

      <MessageGregBubble />
    </div>
  );
}
